import React, { useState, useEffect, useCallback, useRef } from "react";
import Paginate from "../../../CommonComponents/Pagination/Paginate";
import customAPIRequest from "../../../../utils/customAPIRequest";
import { Container } from "react-bootstrap";
import styles from './KdfListingPage.module.css';
import { apiJsonRequest } from "../../../../utils/apiRequest";
import { resolveTags } from "../../../../utils/resolveTags";
import CommonModal from "../../../Modals/CommonModal";
import Button from "../../../Elements/Button";
import { networkService } from "../../../config/axios";
import KdfFilerForm from "./KdfFIlters";
import LoadingScreen from "../../../Elements/LoadingScreen/LoadingScreen";
import { useLocation } from "react-router-dom";
import KdfListCard from "./KdfListCard";

const KdfListPage = ({ props }) => {
    const itemsPerPage = 10;
    const [docData, setDocData] = useState({ loading: true });
    const [getDeleteKDFItem, setDeleteKDFItem] = useState(null);
    const [getCloneKDFItem, setCloneKDFItem] = useState(null);
    const [getPrivateFlagKDFItem, setPrivateFlagKDFItem] = useState(null);
    const { pathname } = useLocation();
    const [currentFilters, setCurrentFilters] = useState({});
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);
    const [activeFilterCount, setActiveFilterCount] = useState(0);
    const [quickBarNode, setQuickBarNode] = useState(null);
    const debounceTimeout = useRef(null);
    const isFetchingRef = useRef(false);
    const initialLoadRef = useRef(false);

const fetchKDFData = useCallback(
    (page = 1, filters = currentFilters, { silent = false } = {}) => {
        // Prevent duplicate calls
        if (isFetchingRef.current) {
            return;
        }
        
        const payload = {
            page,
            per_page: itemsPerPage,
            ...filters,
        };

        if (pathname.includes('kdfs/token')) {
            payload.doc_id = window?.objDoc?.id;
            payload.name = window?.objDoc?.title;
        }

        if (!silent) setDocData(prev => ({ ...prev, loading: true }));
        localStorage.setItem("currentPage", page);
        
        isFetchingRef.current = true;
        customAPIRequest('docs-search.json', 'POST', payload)
            .then((res) => {
                setDocData({ ...res, page, per_page: itemsPerPage, loading: false });
            })
            .catch((err) => {
                console.error(err);
                setDocData({ error: "Something went wrong", loading: false });
            })
            .finally(() => {
                isFetchingRef.current = false;
            });
    },
    [itemsPerPage, pathname, currentFilters]
);

    const handleApplyFilter = useCallback((filters = {}) => {
        if (JSON.stringify(filters) === JSON.stringify(currentFilters)) return;
        setCurrentFilters(filters);
        setActiveFilterCount(Object.keys(filters || {}).length);

        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

        debounceTimeout.current = setTimeout(() => {
            fetchKDFData(1, filters);
        }, 300);
    }, [currentFilters, fetchKDFData]);

    const handleUpdateDocData = useCallback(async (dataToUpdate = [], itemID, skipRefresh = false) => {
        try {
            await apiJsonRequest(`kdfs/${itemID}.json`, 'PATCH', { doc: { tag_ids: dataToUpdate } });
            // Skip refresh for tag updates to avoid loader flash
            if (!skipRefresh) {
                fetchKDFData(docData?.page ?? 1, currentFilters, { force: true });
            }
        } catch (error) {
            console.warn(error);
        }
    }, [fetchKDFData, docData?.page, currentFilters]);

    const handleAddKDFTagsEvent = useCallback(async (value, data) => {
        const resolvedTags = await resolveTags(value, data?.tags || []);
        handleUpdateDocData(resolvedTags.map((tag) => tag.id), data.id, true);
    }, [handleUpdateDocData]);

    const updateRecordData = useCallback((value, data, type) => {
        if (type === 'tags') {
            handleAddKDFTagsEvent(value, data);
        }
    }, [handleAddKDFTagsEvent]);

    const triggerUpdateRecord = useCallback(
        (options) => (options ? fetchKDFData(docData?.page ?? 1, currentFilters, options) : fetchKDFData()),
        [fetchKDFData, docData?.page, currentFilters]
    );

    const handleDeleteKDFItemEvent = async () => {
        const { status } = await networkService(`/kdfs/${getDeleteKDFItem}.json`, 'DELETE');
        if (status === 204) {
            fetchKDFData();
            setDeleteKDFItem(null);
        }
    }

    const handleCloneKDFEvent = async () => {
        const { data } = await networkService(`/docs/clone/${getCloneKDFItem}.json`, 'GET');
        if (data.msg === 'ok') {
            fetchKDFData();
            setCloneKDFItem(null);
        }
    }

    const handleMakePrivateEvent = async () => {
        const { status } = await networkService(`kdfs/${getPrivateFlagKDFItem.id}.json`, 'PATCH', {
            doc_id: getPrivateFlagKDFItem.id,
            private_flag: !(getPrivateFlagKDFItem?.status ?? false),
        });
        if (status === 200) {
            fetchKDFData();
            setPrivateFlagKDFItem(null);
        }
    }

    useEffect(() => {
      // Prevent duplicate initial load calls (React StrictMode)
      // Use local ref to handle StrictMode double-invocation
      // The global deduplication in customAPIRequest will handle simultaneous calls
      if (initialLoadRef.current || isFetchingRef.current) {
        return;
      }
      initialLoadRef.current = true;
      const savedPage = Number(localStorage.getItem("currentPage")) || 1;
      fetchKDFData(savedPage);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div>
            {/* Modals */}
            <CommonModal
                title={getDeleteKDFItem != null ? 'Delete KDF' : 'Clone this KDF'}
                modalStatus={getDeleteKDFItem != null || getCloneKDFItem != null}
                changeActiveStatus={() => {
                    setDeleteKDFItem(null);
                    setCloneKDFItem(null);
                }}
                centered
                classList={'upload-kdf-modal-wrapper alert-modal'}
                footer={
                    <div className="d-flex m-auto">
                        <Button text={'Cancel'} classList={'alert delete-btn'} addonClass={'my-2 mx-2'} onClick={() => {
                            setDeleteKDFItem(null);
                            setCloneKDFItem(null);
                        }} />
                        <Button text={getDeleteKDFItem != null ? 'Delete' : 'Clone'} classList={'success delete-btn'} addonClass={'my-2 mx-2 common-modal-yes-button'} onClick={() => {
                            getDeleteKDFItem != null ? handleDeleteKDFItemEvent() : handleCloneKDFEvent();
                        }} />
                    </div>
                }
            >
                <p style={{ fontSize: '14px' }}>
                    {
                        getDeleteKDFItem != null
                            ? 'Are you sure you want to delete this KDF? This action cannot be undone.'
                            : 'Do you want to continue?'
                    }
                </p>
            </CommonModal>

            <CommonModal
                title={''}
                modalStatus={getPrivateFlagKDFItem != null}
                changeActiveStatus={() => setPrivateFlagKDFItem(null)}
                centered
                classList={'upload-kdf-modal-wrapper alert-modal'}
                footer={
                    <div className="d-flex m-auto">
                        <Button text={'Cancel'} classList={'alert delete-btn'} addonClass={'my-2 mx-2'} onClick={() => setPrivateFlagKDFItem(null)} />
                        <Button text={'Yes'} classList={'success delete-btn'} addonClass={'my-2 mx-2 common-modal-yes-button'} onClick={() => handleMakePrivateEvent()} />
                    </div>
                }
            >
                <p style={{ fontSize: '14px' }}>
                    Hide this KDF from other kinetizine users. Your recipients will still be able to view it via the LaserLINX you share with them.
                </p>
            </CommonModal>

            <div className="header-content-main links-filter-header">
                <div className="doc-filter">
                    <div className="filter-toggle" aria-expanded={isFiltersOpen}>
                        <button
                            type="button"
                            className="filter-toggle-label"
                            onClick={() => setIsFiltersOpen((prev) => !prev)}
                        >
                            <span className="filter-toggle-left">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.5 2.5H14.5L9.5 8.5V13L6.5 14.5V8.5L1.5 2.5Z" stroke="#6b7280" strokeWidth="1.3" strokeLinejoin="round" strokeLinecap="round" />
                                </svg>
                                <h4 className="filter-title">Filters</h4>
                                {activeFilterCount > 0 && (
                                    <span className="filter-count-badge">{activeFilterCount}</span>
                                )}
                            </span>
                        </button>
                        <div
                            className="filters-quick-bar"
                            ref={setQuickBarNode}
                            style={{ display: isFiltersOpen ? "none" : undefined }}
                        />
                        <button
                            type="button"
                            className="filter-toggle-chevron-btn"
                            aria-label={isFiltersOpen ? "Collapse filters" : "Expand filters"}
                            onClick={() => setIsFiltersOpen((prev) => !prev)}
                        >
                            <svg
                                className={`filter-chevron ${isFiltersOpen ? "open" : ""}`}
                                width="14"
                                height="14"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M4 6L8 10L12 6" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>
                    <div className={`filter-body ${isFiltersOpen ? "open" : ""}`}>
                        <div className="filter-body-inner">
                            <KdfFilerForm
                                triggerApplyFilterEvent={handleApplyFilter}
                                loading={docData?.loading}
                                kdfTitleDefault={pathname.includes('kdfs/token') ? window?.objDoc?.title : ''}
                                isOpen={isFiltersOpen}
                                quickBarTarget={quickBarNode}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Container className={styles.listingContainer}>
                {docData?.data?.cnt > docData?.per_page && (
                    <Paginate
                        currentPage={docData?.page}
                        totalPages={Math.ceil(docData?.data?.cnt / docData?.per_page)}
                        onPageChange={(page) => fetchKDFData(page)}
                        totalData={docData?.data?.cnt}
                        itemTitle={docData?.data?.cnt > 1 ? "KDFs" : "KDF"}
                        pageSize={docData?.per_page}
                        currentCount={docData?.data?.docs?.length}
                    />
                )}
                {docData?.loading ? (
                    <LoadingScreen />
                ) : docData?.data?.docs?.length > 0 ? (
                    <>
                        {docData?.data?.docs.map((kdf, index) => (
                            <KdfListCard
                                key={`${kdf?.id}-${index}`}
                                data={kdf}
                                triggerUdateRecord={triggerUpdateRecord}
                                triggerUpdateRecordData={updateRecordData}
                                time_zone_list={props.time_zone_list}
                                triggerDeleteKDF={setDeleteKDFItem}
                                triggerCloneKDF={setCloneKDFItem}
                                triggerMakePrivateEvent={setPrivateFlagKDFItem}
                            />
                        ))}
                        {docData?.data?.cnt > docData?.per_page && (
                            <Paginate
                                currentPage={docData?.page}
                                totalPages={Math.ceil(docData?.data?.cnt / docData?.per_page)}
                                onPageChange={(page) => fetchKDFData(page)}
                                totalData={docData?.data?.cnt}
                                itemTitle={docData?.data?.cnt > 1 ? "KDFs" : "KDF"}
                                pageSize={docData?.per_page}
                                currentCount={docData?.data?.docs?.length}
                            />
                        )}
                    </>
                ) : (
                    docData?.error || "No Record Found"
                )}
            </Container>
        </div>
    );
};

export default KdfListPage;
